/**
 * Account-portal-specific Wilson white-label wiring: brand detection, login
 * background gradient, and the brand-aware app flags (signup disabled,
 * dashboard redirect URL).
 *
 * Shared visual atoms (palette constants, logo SVGs, favicon) live in
 * `@revelio/assets` so the dashboard can consume them too.
 */
import type { CSSProperties } from 'react';

import { WILSON_PALETTE } from '@revelio/assets';

import { environment } from './environment';

export { WilsonFullLogo, wilsonFavicon } from '@revelio/assets';

// ---------------------------------------------------------------------------
// Brand detection
//   1. PUBLIC_FORCE_BRAND env var === 'wilson'
//   2. window.location.hostname starts with 'wilson.'
// ---------------------------------------------------------------------------
function detectWilsonBrand(): boolean {
  if (environment.PUBLIC_FORCE_BRAND?.toLowerCase() === 'wilson') {
    return true;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  return window.location.hostname.toLowerCase().startsWith('wilson.');
}

export const isWilsonBrand = detectWilsonBrand();

// ---------------------------------------------------------------------------
// Brand-aware app flags
// ---------------------------------------------------------------------------
export const isCreateAccountEnabled = !isWilsonBrand;

export const dashboardUrl =
  (isWilsonBrand && environment.PUBLIC_WILSON_REACT_APP_URL) ||
  environment.PUBLIC_REACT_APP_URL;

// ---------------------------------------------------------------------------
// Login background gradient — three soft radials drawn from the Wilson
// palette: light yellow top-right, mint bottom-left, peach bottom-right.
// ---------------------------------------------------------------------------
export const wilsonLoginGradient = `
  radial-gradient(circle at 90% 0%, ${WILSON_PALETTE.lightYellow}D9 0%, ${WILSON_PALETTE.lightYellow}73 12%, ${WILSON_PALETTE.lightYellow}00 32%),
  radial-gradient(140% 80% at 0% 110%, ${WILSON_PALETTE.coverBackground}BF 0%, ${WILSON_PALETTE.coverBackground}59 18%, ${WILSON_PALETTE.coverBackground}00 50%),
  radial-gradient(circle at 100% 100%, ${WILSON_PALETTE.peach}59 0%, ${WILSON_PALETTE.peach}00 28%),
  #ffffff
`;

// ---------------------------------------------------------------------------
// Themed login button override — keeps the redesign's button shape and size
// but swaps Revelio's #25282d for Wilson's brand dark neutral (the same colour
// as the Wilson logo). Only the three colour tokens differ, so this is a
// custom-property override applied to the button; every other button rule
// (size, weight, disabled treatment) still comes from the shared style.
// ---------------------------------------------------------------------------
export const wilsonButtonVars = {
  '--ap-btn-bg': WILSON_PALETTE.darkNeutral,
  '--ap-btn-hover': `${WILSON_PALETTE.darkNeutral}D9`,
  '--ap-btn-active': `${WILSON_PALETTE.darkNeutral}BF`,
} as CSSProperties;
